import { useCallback, useEffect, useState } from 'react';
import {
  addWishlistItem,
  getWishlists,
  removeWishlistByCourseId,
  removeWishlistItem,
} from '../services/wishlists';
import { mapWishlistsResponse } from '../utils/wishlistMappers';

export function useWishlists({ enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchWishlists = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);
    try {
      const res = await getWishlists();
      setItems(mapWishlistsResponse(res));
    } catch (e) {
      setError(e?.message || 'Failed to load favorites');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchWishlists();
  }, [fetchWishlists]);

  const addToWishlist = useCallback(
    async (courseId) => {
      setActionLoading(true);
      try {
        await addWishlistItem(courseId);
        await fetchWishlists();
      } finally {
        setActionLoading(false);
      }
    },
    [fetchWishlists]
  );

  const removeFromWishlist = useCallback(
    async ({ wishlistId, courseId }) => {
      setActionLoading(true);
      try {
        if (wishlistId) {
          await removeWishlistItem(wishlistId);
        } else if (courseId) {
          await removeWishlistByCourseId(courseId);
        }
        setItems((current) =>
          current.filter(
            (item) => item.wishlistId !== wishlistId && item.course.id !== courseId
          )
        );
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  return {
    items,
    loading,
    error,
    actionLoading,
    refetch: fetchWishlists,
    addToWishlist,
    removeFromWishlist,
  };
}
